import { Inbox, Download, ChevronRight, Trash2, Lock as LockIcon } from 'lucide-react';
import CustomSelect from '../../../../../../components/forms/CustomSelect';
import PlanGate, { usePlanAccess } from '../../../../../../components/gating/PlanGate';
import FeatureLocked from '../../../../../../components/gating/FeatureLocked';
import { STATUS_COLORS, format24HourDateOnly, postTripData } from '../../../../utils/postTripHelpers';

const EnquiriesTable = ({
    enquiries,
    loading,
    statusFilter,
    setStatusFilter,
    onExportCSV,
    onSelect,
    onDelete,
    onStatusChange
}) => {
    const { isLocked: isEnquiriesLocked } = usePlanAccess('growth');
    const statusOptions = postTripData.enquiryStatuses || [];
    const sampleGhostEnquiries = postTripData.sampleGhostEnquiries || [];

    return (
        <div className="space-y-6">
            {/* Filter & Export Bar */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pb-4 border-b border-slate-100 animate-in fade-in duration-200">
                <div>
                    <span className="text-xs font-medium text-slate-500">
                        Filter & export enquiries received from travellers.
                    </span>
                </div>
                <div className="flex items-center gap-3 w-full md:w-auto justify-end">
                    <div className="w-full md:w-40">
                        <CustomSelect
                            value={statusFilter}
                            onChange={(val) => setStatusFilter(val)}
                            options={statusOptions}
                            placeholder="All Statuses"
                        />
                    </div>

                    <PlanGate
                        requiredPlan="growth"
                        featureName="Post Trip Enquiries"
                        tooltipText="Exporting enquiries requires the Growth Plan."
                        showLockIcon={false}
                        wrapperClassName="w-full md:w-auto"
                    >
                        {({ isLocked, onClick }) => (
                            <button
                                type="button"
                                onClick={isLocked ? onClick : onExportCSV}
                                className="px-6 py-2.5 bg-[#067978] text-white rounded-xl text-sm font-semibold hover:bg-[#056665] shadow-lg shadow-[#067978]/20 transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap w-full md:w-auto"
                            >
                                <Download size={15} />
                                <span>Export CSV</span>
                                {isLocked && <LockIcon size={14} className="ml-1 text-white/90 shrink-0" />}
                            </button>
                        )}
                    </PlanGate>
                </div>
            </div>

            {/* Table Area */}
            <div className="relative min-h-[480px] pb-4">
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#067978]"></div>
                    </div>
                ) : isEnquiriesLocked ? (
                    /* Sample Ghost Table in Background */
                    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden select-none pointer-events-none opacity-60 filter blur-[0.4px]">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm text-slate-600">
                                <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase font-bold text-slate-500 tracking-wider">
                                    <tr>
                                        <th className="px-6 py-4">Traveller</th>
                                        <th className="px-6 py-4">Source Package</th>
                                        <th className="px-6 py-4">Received</th>
                                        <th className="px-6 py-4">Status</th>
                                        <th className="px-6 py-4 text-right">Details</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {sampleGhostEnquiries.map((enq) => (
                                        <tr key={enq._id} className="hover:bg-slate-50/80 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="font-bold text-slate-800">{enq.traveller_name}</div>
                                                <div className="text-xs text-[#067978] bg-teal-50 border border-teal-100/50 rounded px-1.5 py-0.5 inline-block font-mono font-semibold mt-1 uppercase">
                                                    ID: {enq.traveller_phone}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="font-semibold text-slate-700">
                                                    {enq.source_package_id?.title || 'Open Enquiry'}
                                                </div>
                                                {enq.source_package_id?.destination_tag && (
                                                    <span className="text-xs text-slate-400">{enq.source_package_id.destination_tag}</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-xs text-slate-500 font-medium">
                                                {format24HourDateOnly(enq.createdAt)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize ${STATUS_COLORS[enq.status] || 'bg-teal-50 text-[#067978] border-teal-100/70'}`}>
                                                    {enq.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <button className="p-2 text-slate-400">
                                                    <ChevronRight size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : enquiries.length === 0 ? (
                    <div className="relative w-full overflow-hidden pb-4">
                        {/* 1. Sample Ghost Table in Background */}
                        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden select-none opacity-40 filter blur-[0.3px]">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm text-slate-600">
                                    <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase font-bold text-slate-500 tracking-wider">
                                        <tr>
                                            <th className="px-6 py-4">Traveller</th>
                                            <th className="px-6 py-4">Source Package</th>
                                            <th className="px-6 py-4">Received</th>
                                            <th className="px-6 py-4">Status</th>
                                            <th className="px-6 py-4 text-right">Details</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {sampleGhostEnquiries.map((enq) => (
                                            <tr key={enq._id}>
                                                <td className="px-6 py-4">
                                                    <div className="font-bold text-slate-800">{enq.traveller_name}</div>
                                                    <div className="text-xs text-[#067978] bg-teal-50 border border-teal-100/50 rounded px-1.5 py-0.5 inline-block font-mono font-semibold mt-1 uppercase">
                                                        ID: {enq.traveller_phone}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="font-semibold text-slate-700">
                                                        {enq.source_package_id?.title || 'Open Enquiry'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-xs text-slate-500 font-medium">
                                                    {format24HourDateOnly(enq.createdAt)}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium border bg-teal-50 text-[#067978] border-teal-100/70 capitalize">
                                                        {enq.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <button className="p-2 text-slate-400">
                                                        <ChevronRight size={18} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* 2. Elevated Floating Empty State Card */}
                        <div className="relative z-20 -mt-44 sm:-mt-52 md:-mt-56 px-3 sm:px-6 flex justify-center items-center">
                            <div className="w-full max-w-md p-6 sm:p-8 text-center bg-white rounded-3xl border border-slate-200/90 shadow-sm relative backdrop-blur-md">
                                <div className="w-14 h-14 bg-teal-50 text-[#067978] rounded-2xl flex items-center justify-center mx-auto mb-4 border border-teal-100/80 shadow-2xs">
                                    <Inbox size={28} />
                                </div>
                                <h2 className="text-xl sm:text-2xl font-normal text-slate-900 tracking-tight">
                                    No enquiries received yet
                                </h2>
                                <p className="text-xs sm:text-[13px] text-slate-500 leading-relaxed font-normal max-w-sm mx-auto mt-2">
                                    Enquiries submitted by travellers from their post-trip app will appear here.
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm text-slate-600">
                                <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase font-bold text-slate-500 tracking-wider">
                                    <tr>
                                        <th className="px-6 py-4">Traveller</th>
                                        <th className="px-6 py-4">Source Package</th>
                                        <th className="px-6 py-4">Received</th>
                                        <th className="px-6 py-4">Status</th>
                                        <th className="px-6 py-4 text-right">Details</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {enquiries.map((enq) => (
                                        <tr key={enq._id} className="hover:bg-slate-50/80 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="font-bold text-slate-800">{enq.traveller_name}</div>
                                                <div className="text-xs text-[#067978] bg-teal-50 border border-teal-100/50 rounded px-1.5 py-0.5 inline-block font-mono font-semibold mt-1 uppercase">
                                                    ID: {enq.traveller_phone}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="font-semibold text-slate-700">
                                                    {enq.source_package_id?.title || 'Open Enquiry'}
                                                </div>
                                                {enq.source_package_id?.destination_tag && (
                                                    <span className="text-xs text-slate-400">{enq.source_package_id.destination_tag}</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-xs text-slate-500 font-medium">
                                                {format24HourDateOnly(enq.createdAt)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <select
                                                    value={enq.status}
                                                    onChange={(e) => onStatusChange && onStatusChange(enq._id, e.target.value)}
                                                    className={`px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize cursor-pointer focus:outline-none ${STATUS_COLORS[enq.status] || 'bg-teal-50 text-[#067978] border-teal-100/70'}`}
                                                >
                                                    <option value="new">New</option>
                                                    <option value="contacted">Contacted</option>
                                                    <option value="quoted">Quoted</option>
                                                </select>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => onSelect(enq)}
                                                        className="p-2 hover:bg-slate-100 text-slate-500 hover:text-[#067978] rounded-lg transition-colors cursor-pointer"
                                                        title="View Details"
                                                    >
                                                        <ChevronRight size={18} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => onDelete(enq._id)}
                                                        className="p-2 hover:bg-rose-50 text-rose-600 hover:text-rose-700 rounded-lg transition-colors cursor-pointer"
                                                        title="Delete Enquiry"
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {isEnquiriesLocked && (
                    <FeatureLocked featureKey="enquiries" overlay={true} />
                )}
            </div>
        </div>
    );
};

export default EnquiriesTable;
